/**
 * Pure keydown matcher for the shortcut catalog (mod chords + two-key sequences).
 */

import {
  KEYBOARD_SHORTCUTS,
  SHORTCUT_SEQUENCE_MS,
  shortcutAllowed,
  type ShortcutDef,
} from './catalog';

export type SequencePending = {
  first: string;
  expiresAt: number;
};

export type MatchKeydownInput = {
  event: Pick<
    KeyboardEvent,
    'key' | 'ctrlKey' | 'metaKey' | 'altKey' | 'repeat'
  >;
  pending: SequencePending | null;
  now: number;
  hasPermission: (permission: string) => boolean;
  isAdmin: boolean;
  /** Skip sequence / non-spotlight shortcuts when typing in a field. */
  inEditable: boolean;
  /** When Spotlight is already open, still swallow ⌘K. */
  spotlightOpen: boolean;
  /** When help is open, ⌘/ toggles close (matched as open-help). */
  helpOpen: boolean;
};

export type MatchKeydownResult =
  | { type: 'ignore'; pending: SequencePending | null }
  | { type: 'pending'; pending: SequencePending }
  | { type: 'match'; shortcut: ShortcutDef; pending: null };

const allowedList = (
  hasPermission: (permission: string) => boolean,
  isAdmin: boolean,
): ShortcutDef[] =>
  KEYBOARD_SHORTCUTS.filter((s) => shortcutAllowed(s, hasPermission, isAdmin));

const isMod = (event: MatchKeydownInput['event']): boolean =>
  event.ctrlKey || event.metaKey;

export const matchKeydown = (input: MatchKeydownInput): MatchKeydownResult => {
  const {
    event,
    pending,
    now,
    hasPermission,
    isAdmin,
    inEditable,
    spotlightOpen,
    helpOpen,
  } = input;

  if (event.repeat) {
    return { type: 'ignore', pending };
  }

  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
  const allowed = allowedList(hasPermission, isAdmin);

  // Modifier chords (⌘K, ⌘/)
  if (isMod(event) && !event.altKey) {
    const modHit = allowed.find(
      (s) => s.chord.kind === 'mod' && s.chord.key === key,
    );
    if (!modHit) {
      return { type: 'ignore', pending: null };
    }

    if (modHit.run.type === 'open-spotlight') {
      if (inEditable && !spotlightOpen) {
        return { type: 'ignore', pending: null };
      }
      return { type: 'match', shortcut: modHit, pending: null };
    }

    if (modHit.run.type === 'open-help') {
      // Allow toggle even from some fields; skip pure text inputs only when closed.
      if (inEditable && !helpOpen) {
        return { type: 'ignore', pending: null };
      }
      return { type: 'match', shortcut: modHit, pending: null };
    }

    if (inEditable) {
      return { type: 'ignore', pending: null };
    }
    return { type: 'match', shortcut: modHit, pending: null };
  }

  // Sequences never fire with modifiers or while typing
  if (event.altKey || event.ctrlKey || event.metaKey || inEditable) {
    return { type: 'ignore', pending: null };
  }

  if (key === 'Escape') {
    return { type: 'ignore', pending: null };
  }

  const livePending =
    pending && pending.expiresAt > now ? pending : null;

  if (livePending) {
    const seqHit = allowed.find(
      (s) =>
        s.chord.kind === 'sequence' &&
        s.chord.keys[0] === livePending.first &&
        s.chord.keys[1] === key,
    );
    if (seqHit) {
      return { type: 'match', shortcut: seqHit, pending: null };
    }
    // Wrong second key — cancel, do not start a new sequence on this same key
    // unless it is a valid first key (handled below only when no pending).
    return { type: 'ignore', pending: null };
  }

  if (key.length !== 1 || !/[a-z]/.test(key)) {
    return { type: 'ignore', pending: null };
  }

  const isFirst = allowed.some(
    (s) => s.chord.kind === 'sequence' && s.chord.keys[0] === key,
  );
  if (!isFirst) {
    return { type: 'ignore', pending: null };
  }

  return {
    type: 'pending',
    pending: { first: key, expiresAt: now + SHORTCUT_SEQUENCE_MS },
  };
};
